import { defineStore } from 'pinia'
import axiosOverlayConnector from '@/services/axiosOverlayConnector.js'
import useCategorieDepenseStore from '@/stores/categorieDepense.js'

export default defineStore('depense', {
  state: () => ({
    depenseByLast12Month: null,
    depenseByOneCategorieSum: null,
    depenseByOneCategorie: null,
    depenseByOneCategorieFiltered: null,

    loading: 0,
    loadingDepenseByLast12Month: 0,
    loadingDepenseByOneCategorie: 0,

    loadingDepenseByOneCategorieFiltered: 0
  }),

  getters: {
    isLoading: (state) => {
      return state.loading > 0
    },
    isLoadingDepenseByLast12Month: (state) => {
      return state.loadingDepenseByLast12Month > 0
    },
    isLoadingDepenseByOneCategorieFiltered: (state) => {
      return state.loadingDepenseByOneCategorieFiltered > 0
    }
  },

  actions: {
    updateDepenseByLast12MonthTotalMontant(index, newTotalMontant) {
      if (this.depenseByLast12Month && this.depenseByLast12Month[index]) {
        this.depenseByLast12Month[index].totalMontant = newTotalMontant
      }
    },

    updateDepenseByOneCategorieSum(index, newTotalMontant) {
      if (this.depenseByOneCategorieSum && this.depenseByOneCategorieSum[index]) {
        this.depenseByOneCategorieSum[index].totalMontant = newTotalMontant
      }
    },

    getDepenseByOneCategorieIndex(month, year) {
      if (!this.depenseByOneCategorieSum) return -1

      return this.depenseByOneCategorieSum.findIndex((d) => d.month === month && d.year === year)
    },

    loadDepenseByOneCategorieByPeriodeFiltered(categorieId, periode) {
      this.loadingDepenseByOneCategorieFiltered++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/depenses/by-categorie'

        const config = {
          url: url,
          method: 'POST',
          data: {
            id_categorie: categorieId,
            date_debut: periode[0],
            date_fin: periode[1]
          }
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            this.depenseByOneCategorieFiltered = response.data
            resolve(response)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loadingDepenseByOneCategorieFiltered--
          })
      })
    },

    loadDepenseByOneCategorie(categorieId) {
      this.loadingDepenseByOneCategorie++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/depenses/by-categorie'

        const config = {
          url: url,
          method: 'POST',
          data: {
            id_categorie: categorieId
          }
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            this.depenseByOneCategorie = response.data
            resolve(response)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loadingDepenseByOneCategorie--
          })
      })
    },

    loadDepenseSumByOneCategorie(categorieId) {
      this.loadingDepenseByOneCategorie++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/depenses/sum-by-one-categorie/' + categorieId

        const config = {
          url: url,
          method: 'GET'
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            this.depenseByOneCategorieSum = response.data
            resolve(response)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loadingDepenseByOneCategorie--
          })
      })
    },

    loadDepenseByLast12Month() {
      this.loadingDepenseByLast12Month++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/depenses/sum-by-month'

        const config = {
          url: url,
          method: 'GET'
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            this.depenseByLast12Month = response.data
            resolve(response)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loadingDepenseByLast12Month--
          })
      })
    },

    getDepenseByLast12MonthIndex(month, year) {
      if (!this.depenseByLast12Month) return -1

      return this.depenseByLast12Month.findIndex((d) => d.month === month && d.year === year)
    },

    updateDepense(depenseData) {
      const { addDepenseToCategorie, removeDepenseFromCategorie } = useCategorieDepenseStore()

      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/depenses/' + depenseData.id

        const config = {
          url: url,
          method: 'PUT',
          data: {
            ...depenseData
          }
        }

        return axiosOverlayConnector(config)
          .then(() => {
            // Récupération du mois et de l'annee de l'ancienne dépense
            const lastDate = new Date(Number(depenseData.lastDate) * 1000)
            const lastMonth = lastDate.getMonth() + 1
            const lastYear = lastDate.getFullYear()

            // Récupération du mois et de l'année de la dépense modifiée
            const newDate = new Date(Number(depenseData.date) * 1000)
            const newMonth = newDate.getMonth() + 1
            const newYear = newDate.getFullYear()

            const lastMonthIndex = this.getDepenseByLast12MonthIndex(lastMonth, lastYear)
            const newMonthIndex = this.getDepenseByLast12MonthIndex(newMonth, newYear)

            // Index pour depenseByOneCategorieSum (détails de catégorie) - seulement si la liste existe
            const lastMonthIndexCategorie =
              this.depenseByOneCategorieSum && this.depenseByOneCategorieSum.length > 0
                ? this.getDepenseByOneCategorieIndex(lastMonth, lastYear)
                : -1
            const newMonthIndexCategorie =
              this.depenseByOneCategorieSum && this.depenseByOneCategorieSum.length > 0
                ? this.getDepenseByOneCategorieIndex(newMonth, newYear)
                : -1

            const categorieChanged = depenseData.idCategorie !== depenseData.lastIdCategorie

            // Update des infos dans la chart des 12 derniers mois
            if (lastMonth !== newMonth || lastYear !== newYear) {
              // Si le mois ou l'année ont changé, on met à jour les deux mois concernés

              // Soustraire l'ancien montant de l'ancien mois
              if (lastMonthIndex !== -1) {
                const oldMonthTotal = this.depenseByLast12Month[lastMonthIndex].totalMontant
                const newOldMonthTotal = Math.max(0, oldMonthTotal - depenseData.lastMontant)
                this.updateDepenseByLast12MonthTotalMontant(lastMonthIndex, newOldMonthTotal)
              }

              // Ajouter le nouveau montant au nouveau mois
              if (newMonthIndex !== -1) {
                const newMonthTotal = this.depenseByLast12Month[newMonthIndex].totalMontant
                const updatedNewMonthTotal = Math.max(0, newMonthTotal + depenseData.montant)
                this.updateDepenseByLast12MonthTotalMontant(newMonthIndex, updatedNewMonthTotal)
              }

              // Mettre à jour depenseByOneCategorieSum (chart catégorie affichée) - seulement si elle existe
              // Si la catégorie a changé : on soustrait uniquement l'ancien montant (la dépense quitte la catégorie)
              // Si la catégorie est identique : on soustrait l'ancien mois et on ajoute au nouveau mois
              if (this.depenseByOneCategorieSum && this.depenseByOneCategorieSum.length > 0) {
                if (lastMonthIndexCategorie !== -1) {
                  const oldMonthTotalCategorie =
                    this.depenseByOneCategorieSum[lastMonthIndexCategorie].totalMontant
                  const newOldMonthTotalCategorie = Math.max(
                    0,
                    oldMonthTotalCategorie - depenseData.lastMontant
                  )
                  this.updateDepenseByOneCategorieSum(
                    lastMonthIndexCategorie,
                    newOldMonthTotalCategorie
                  )
                }

                if (!categorieChanged && newMonthIndexCategorie !== -1) {
                  const newMonthTotalCategorie =
                    this.depenseByOneCategorieSum[newMonthIndexCategorie].totalMontant
                  const updatedNewMonthTotalCategorie = Math.max(
                    0,
                    newMonthTotalCategorie + depenseData.montant
                  )
                  this.updateDepenseByOneCategorieSum(
                    newMonthIndexCategorie,
                    updatedNewMonthTotalCategorie
                  )
                }
              }
            } else {
              // Si le mois et l'année sont identiques, on met à jour uniquement le mois concerné
              if (newMonthIndex !== -1) {
                const currentTotal = this.depenseByLast12Month[newMonthIndex].totalMontant
                const newCalculatedMontant =
                  currentTotal - depenseData.lastMontant + depenseData.montant
                this.updateDepenseByLast12MonthTotalMontant(
                  newMonthIndex,
                  Math.max(0, newCalculatedMontant)
                )
              }

              // Mettre à jour depenseByOneCategorieSum (chart catégorie affichée) - seulement si elle existe
              // Si la catégorie a changé : soustraire uniquement l'ancien montant
              // Si la catégorie est identique : soustraire l'ancien et ajouter le nouveau
              if (
                this.depenseByOneCategorieSum &&
                this.depenseByOneCategorieSum.length > 0 &&
                newMonthIndexCategorie !== -1
              ) {
                const currentTotalCategorie =
                  this.depenseByOneCategorieSum[newMonthIndexCategorie].totalMontant
                const newCalculatedMontantCategorie = categorieChanged
                  ? Math.max(0, currentTotalCategorie - depenseData.lastMontant)
                  : Math.max(
                      0,
                      currentTotalCategorie - depenseData.lastMontant + depenseData.montant
                    )
                this.updateDepenseByOneCategorieSum(
                  newMonthIndexCategorie,
                  newCalculatedMontantCategorie
                )
              }
            }

            // Suppression de l'ancienne dépense et ajout de la nouvelle
            removeDepenseFromCategorie(depenseData.lastIdCategorie, depenseData.id)
            addDepenseToCategorie(depenseData.idCategorie, depenseData)

            // Mettre à jour depenseByOneCategorie (détails catégorie)
            if (this.depenseByOneCategorie) {
              const idx = this.depenseByOneCategorie.findIndex((d) => d.id === depenseData.id)
              if (idx !== -1) {
                if (depenseData.idCategorie !== depenseData.lastIdCategorie) {
                  // La catégorie a changé : retirer la dépense de la liste
                  this.depenseByOneCategorie = this.depenseByOneCategorie.filter(
                    (d) => d.id !== depenseData.id
                  )
                } else {
                  this.depenseByOneCategorie[idx] = {
                    ...this.depenseByOneCategorie[idx],
                    libelle: depenseData.libelle,
                    montant: depenseData.montant,
                    date: depenseData.date
                  }
                  this.depenseByOneCategorie = [...this.depenseByOneCategorie]
                }
              }
            }

            // Mettre à jour depenseByOneCategorieFiltered (liste filtrée)
            if (this.depenseByOneCategorieFiltered) {
              const idxFiltered = this.depenseByOneCategorieFiltered.findIndex(
                (d) => d.id === depenseData.id
              )
              if (idxFiltered !== -1) {
                if (depenseData.idCategorie !== depenseData.lastIdCategorie) {
                  this.depenseByOneCategorieFiltered = this.depenseByOneCategorieFiltered.filter(
                    (d) => d.id !== depenseData.id
                  )
                } else {
                  this.depenseByOneCategorieFiltered[idxFiltered] = {
                    ...this.depenseByOneCategorieFiltered[idxFiltered],
                    libelle: depenseData.libelle,
                    montant: depenseData.montant,
                    date: depenseData.date
                  }
                  this.depenseByOneCategorieFiltered = [...this.depenseByOneCategorieFiltered]
                }
              }
            }

            resolve()
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },
    deleteDepense(categorieId, depenseId) {
      const categorieDepenseStore = useCategorieDepenseStore()

      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/depenses/' + depenseId

        const config = {
          url: url,
          method: 'DELETE'
        }

        return axiosOverlayConnector(config)
          .then(() => {
            // Récupérer les infos de la dépense avant suppression
            // Fallback sur depenseByOneCategorie si categorieDepenses n'est pas chargé (cas détails catégorie)
            const depense =
              categorieDepenseStore.categorieDepenses
                ?.find((c) => c.idCategorie === categorieId)
                ?.depenses.find((d) => d.id === depenseId) ??
              this.depenseByOneCategorie?.find((d) => d.id === depenseId) ??
              this.depenseByOneCategorieFiltered?.find((d) => d.id === depenseId)

            if (depense) {
              // Récupérer le mois et l'année de la dépense supprimée
              const depenseDate = new Date(Number(depense.date) * 1000)
              const depenseMonth = depenseDate.getMonth() + 1
              const depenseYear = depenseDate.getFullYear()

              // Trouver l'index correspondant dans depenseByLast12Month
              const monthIndex = this.getDepenseByLast12MonthIndex(depenseMonth, depenseYear)

              // Update chart si le mois existe dans la liste
              if (monthIndex !== -1) {
                const currentTotal = this.depenseByLast12Month[monthIndex].totalMontant
                const newMontant = Math.max(0, currentTotal - depense.montant)
                this.updateDepenseByLast12MonthTotalMontant(monthIndex, newMontant)
              }

              // Trouver l'index correspondant dans depenseByOneCategorieSum (détails catégorie)
              // Seulement si la liste existe et n'est pas vide
              if (this.depenseByOneCategorieSum && this.depenseByOneCategorieSum.length > 0) {
                const monthIndexCategorie = this.getDepenseByOneCategorieIndex(
                  depenseMonth,
                  depenseYear
                )

                // Update chart détails catégorie si le mois existe dans la liste
                if (monthIndexCategorie !== -1) {
                  const currentTotalCategorie =
                    this.depenseByOneCategorieSum[monthIndexCategorie].totalMontant
                  const newMontantCategorie = Math.max(0, currentTotalCategorie - depense.montant)
                  this.updateDepenseByOneCategorieSum(monthIndexCategorie, newMontantCategorie)
                }
              }
            }

            // Suppression de la dépense de la catégorie correspondante
            categorieDepenseStore.removeDepenseFromCategorie(categorieId, depenseId)

            // Mettre à jour depenseByOneCategorie (détails catégorie)
            if (this.depenseByOneCategorie) {
              this.depenseByOneCategorie = this.depenseByOneCategorie.filter(
                (d) => d.id !== depenseId
              )
            }

            // Mettre à jour depenseByOneCategorieFiltered (liste filtrée)
            if (this.depenseByOneCategorieFiltered) {
              this.depenseByOneCategorieFiltered = this.depenseByOneCategorieFiltered.filter(
                (d) => d.id !== depenseId
              )
            }

            resolve()
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    }
  }
})
